import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Avatar } from "../components/Layout";
import { ArrowLeft, Lock, Sparkles, BookOpen, Compass } from "lucide-react";

const Chip = ({ children }) => (
  <span className="px-3 py-1.5 rounded-full bg-secondary text-xs font-medium">{children}</span>
);

export default function MemberProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    api.get(`/members/${userId}`).then((r) => setMember(r.data)).catch(() => setBlocked(true));
  }, [userId]);

  if (blocked) {
    return (
      <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3" data-testid="member-profile-blocked">
        <Lock className="h-8 w-8 text-primary mx-auto" />
        <p className="font-display text-xl font-medium">This profile stays within its circle</p>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">You can view people who share a pod or community with you.</p>
      </div>
    );
  }
  if (!member) return <div className="animate-pulse text-muted-foreground text-sm">Loading profile…</div>;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="member-profile-page">
      <button data-testid="member-profile-back" onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="bg-card border border-border rounded-2xl p-8 flex items-start gap-6" data-testid="member-profile-header">
        <Avatar user={member} size="h-24 w-24" />
        <div className="space-y-1.5 min-w-0">
          <h2 className="font-display text-3xl font-medium">{member.name}</h2>
          {member.title && <p className="text-base text-muted-foreground" data-testid="member-profile-title">{member.title}</p>}
          <p className="text-sm text-muted-foreground">
            {[member.company, member.industry, member.chapter_name].filter(Boolean).join(" · ")}
          </p>
          {member.availability && (
            <span className="inline-block px-2.5 py-1 rounded-full bg-sage-pale text-[11px] font-semibold">{member.availability}</span>
          )}
        </div>
      </div>

      {member.bio && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-2" data-testid="member-profile-bio">
          <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
        </section>
      )}

      {(member.what_i_know || (member.know_tags || []).length > 0) && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="member-profile-know">
          <h3 className="font-display text-lg font-medium flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> What I Know</h3>
          {member.what_i_know && <p className="text-sm text-muted-foreground leading-relaxed">{member.what_i_know}</p>}
          {(member.know_tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2">{member.know_tags.map((t) => <Chip key={t}>{t}</Chip>)}</div>
          )}
        </section>
      )}

      {(member.hyphenates || []).length > 0 && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="member-profile-hyphenates">
          <h3 className="font-display text-lg font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> My Hyphenates</h3>
          <div className="flex flex-wrap gap-2">{member.hyphenates.map((t) => <Chip key={t}>{t}</Chip>)}</div>
        </section>
      )}

      {(member.exploring || []).length > 0 && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="member-profile-exploring">
          <h3 className="font-display text-lg font-medium flex items-center gap-2"><Compass className="h-4 w-4 text-primary" /> Exploring right now</h3>
          <div className="flex flex-wrap gap-2">{member.exploring.map((t) => <Chip key={t}>{t}</Chip>)}</div>
        </section>
      )}

      {(member.values || []).length > 0 && (
        <section className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="member-profile-values">
          <h3 className="font-display text-lg font-medium">Values</h3>
          <div className="flex flex-wrap gap-2">{member.values.map((t) => <Chip key={t}>{t}</Chip>)}</div>
        </section>
      )}
    </div>
  );
}
